import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Menu from '@mui/material/Menu';
import InputBase from '@mui/material/InputBase';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Button from '@components/Button';
import { SelectableChip } from '@components/Chip';
import GoogleMap from '@components/GoogleMap';
import { ModalBase } from '@components/Modal';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { alertActions } from '@actions';
import { analyticsService } from '@services';
import { usePopupZIndex } from '@components/Modal/popupLayer';
import { SectionHeading, ChipsWrapper } from './visibilityShared';

const StyledModal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 560,
        '@media (max-width: 1024px)': {
            width: '100%',
        },
    },
    '& .modal-base-content': {
        padding: '16px 20px',
    },
});

const METERS_PER_DEGREE_LAT = 111320;
const MAX_KEYWORD_LENGTH = 100;
const EXCLUDED_PIN_COLOR = '#9E9E9E';

const cellKey = (row, col) => `${row}:${col}`;

const draftFrom = settings => ({
    gridSize: settings.gridSize,
    gridSpacingMeters: settings.gridSpacingMeters,
    excluded: (settings.excludedCells || []).map(cell => cellKey(cell.row, cell.col)),
    keywords: [...settings.keywords],
});

// Mirrors the backend VisibilityGrid math so a size/spacing change previews the new pin
// layout without a round-trip per change.
const buildPreviewPins = (center, gridSize, spacingMeters, excludedSet) => {
    const mid = (gridSize - 1) / 2;
    const pins = [];
    for (let row = 0; row < gridSize; row++) {
        for (let col = 0; col < gridSize; col++) {
            const excluded = excludedSet.has(cellKey(row, col));
            pins.push({
                row,
                col,
                lat: center.lat + ((row - mid) * spacingMeters) / METERS_PER_DEGREE_LAT,
                lng:
                    center.lng +
                    ((col - mid) * spacingMeters) / (METERS_PER_DEGREE_LAT * Math.cos((center.lat * Math.PI) / 180)),
                label: excluded ? '×' : '',
                color: excluded ? EXCLUDED_PIN_COLOR : 'var(--primary-main)',
            });
        }
    }
    return pins;
};

const VisibilitySettingsModal = ({ open, onClose, locationId, locationName, onSaved }) => {
    const { t } = useTranslation();
    const popupZIndex = usePopupZIndex();
    const dispatch = useDispatch();
    const [request, setRequest] = useState({ loading: true });
    const [draft, setDraft] = useState(null);
    const [keywordInput, setKeywordInput] = useState('');
    const [addAnchorEl, setAddAnchorEl] = useState(null);
    const [saving, setSaving] = useState(false);
    const requestToken = useRef(0);

    useEffect(() => {
        // Bumping the token on EVERY open/target transition (including close) invalidates
        // in-flight loads AND saves from a previous target.
        const token = ++requestToken.current;
        if (!open || !locationId) return;
        setRequest({ loading: true });
        setDraft(null);
        setKeywordInput('');
        setAddAnchorEl(null);
        setSaving(false);
        analyticsService.getVisibilitySettings(locationId).then(
            data => {
                if (token !== requestToken.current) return;
                setRequest({ data });
                setDraft(draftFrom(data));
            },
            () => {
                if (token !== requestToken.current) return;
                setRequest({ error: true });
            },
        );
    }, [open, locationId]);

    const settings = request.data;
    const editable = !!settings && !!draft;

    // The anchor every draft geometry previews around; null = no resolvable coordinates,
    // so there is nothing to plot pins on.
    const center = useMemo(
        () =>
            settings?.centerLatitude != null && settings?.centerLongitude != null
                ? { lat: settings.centerLatitude, lng: settings.centerLongitude }
                : null,
        [settings],
    );
    const excludedSet = useMemo(() => new Set(draft?.excluded ?? []), [draft]);
    const pendingKeyword = keywordInput.trim().toLowerCase();

    const markers = useMemo(() => {
        if (!center || !editable) return [];
        return buildPreviewPins(center, draft.gridSize, draft.gridSpacingMeters, excludedSet);
    }, [center, editable, draft, excludedSet]);

    const togglePin = pin => {
        if (saving || !draft) return;
        const key = cellKey(pin.row, pin.col);
        const isExcluded = excludedSet.has(key);
        if (!isExcluded && draft.excluded.length >= draft.gridSize * draft.gridSize - 1) {
            dispatch(alertActions.error(t('AnalyticsVisibility.keepOnePin')));
            return;
        }
        setDraft(prev => ({
            ...prev,
            excluded: isExcluded ? prev.excluded.filter(item => item !== key) : [...prev.excluded, key],
        }));
    };

    const closeAddMenu = () => {
        setAddAnchorEl(null);
        setKeywordInput('');
    };

    const addKeyword = () => {
        if (!pendingKeyword) return;
        if (!draft.keywords.includes(pendingKeyword) && draft.keywords.length < settings.maxKeywords) {
            setDraft(prev => ({ ...prev, keywords: [...prev.keywords, pendingKeyword] }));
        }
        closeAddMenu();
    };

    const removeKeyword = text =>
        setDraft(prev => ({ ...prev, keywords: prev.keywords.filter(keyword => keyword !== text) }));

    const onSave = () => {
        if (draft.keywords.length === 0) return;
        const token = requestToken.current;
        setSaving(true);
        analyticsService
            .updateVisibilitySettings(locationId, {
                gridSize: draft.gridSize,
                gridSpacingMeters: draft.gridSpacingMeters,
                excludedCells: draft.excluded.map(key => {
                    const [row, col] = key.split(':').map(Number);
                    return { row, col };
                }),
                keywords: draft.keywords,
            })
            .then(
                data => {
                    if (token !== requestToken.current) return;
                    setSaving(false);
                    dispatch(
                        alertActions.success(
                            t(
                                data?.firstScanStarted
                                    ? 'AnalyticsVisibility.settingsSavedScanStarted'
                                    : 'AnalyticsVisibility.settingsSaved',
                            ),
                        ),
                    );
                    onSaved();
                },
                saveError => {
                    if (token !== requestToken.current) return;
                    setSaving(false);
                    if (!saveError?._serverError) {
                        dispatch(alertActions.error(t('apiErrors.something_wrong')));
                    }
                },
            );
    };

    const renderContent = () => {
        if (request.loading) {
            return <Skeleton variant="rectangular" width="100%" height={400} />;
        }
        if (request.error || !editable) {
            return (
                <Typography variant="body1" align="center" sx={{ display: 'block', padding: '32px 0' }}>
                    {t('AnalyticsVisibility.loadError')}
                </Typography>
            );
        }

        return (
            <>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: '16px' }}>
                    {t('AnalyticsVisibility.settingsApplyNote')}
                </Typography>
                <SectionHeading sx={{ marginTop: 0 }}>{t('AnalyticsVisibility.trackedKeywords')}</SectionHeading>
                <ChipsWrapper>
                    {draft.keywords.map(text => (
                        <SelectableChip
                            key={text}
                            disabled={saving}
                            label={text}
                            onDelete={() => removeKeyword(text)}
                        />
                    ))}
                    {draft.keywords.length < settings.maxKeywords && (
                        <Button
                            type="button"
                            variant="text"
                            sx={{ height: 30, minWidth: 0, padding: '4px 8px' }}
                            disabled={saving}
                            onClick={event => setAddAnchorEl(event.currentTarget)}
                        >
                            <AddRoundedIcon sx={{ fontSize: 16 }} />
                            {t('AnalyticsVisibility.addKeywordLabel')}
                        </Button>
                    )}
                </ChipsWrapper>
                {draft.keywords.length === 0 && (
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--error-main)', display: 'block', marginTop: '8px' }}
                    >
                        {t('AnalyticsVisibility.keywordsRequired')}
                    </Typography>
                )}
                <Menu
                    anchorEl={addAnchorEl}
                    open={!!addAnchorEl}
                    onClose={closeAddMenu}
                    autoFocus={false}
                    sx={{ zIndex: popupZIndex, '.MuiMenu-paper': { borderRadius: '8px', minWidth: 260 } }}
                >
                    <div style={{ padding: '4px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                        <InputBase
                            autoFocus
                            placeholder={t('AnalyticsVisibility.addKeywordLabel')}
                            value={keywordInput}
                            onChange={event => setKeywordInput(event.target.value)}
                            inputProps={{ maxLength: MAX_KEYWORD_LENGTH }}
                            onKeyDown={event => {
                                event.stopPropagation();
                                if (event.key === 'Enter') {
                                    addKeyword();
                                }
                            }}
                            sx={{ fontSize: 14, flex: 1 }}
                        />
                        <Button
                            type="button"
                            variant="text"
                            sx={{ height: 24, minWidth: 0, padding: '4px 8px', whiteSpace: 'nowrap' }}
                            disabled={!pendingKeyword}
                            onClick={addKeyword}
                        >
                            {t('buttons.add')}
                        </Button>
                    </div>
                </Menu>
                <SectionHeading>{t('AnalyticsVisibility.gridSettings')}</SectionHeading>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Select
                        label={t('AnalyticsVisibility.gridSizeLabel')}
                        options={settings.gridSizeOptions.map(size => ({ value: size, label: `${size}×${size}` }))}
                        value={draft.gridSize}
                        disabled={saving}
                        onChange={event =>
                            // Cell indices don't survive a resize — drop exclusions with the
                            // grid they were picked on.
                            setDraft(prev => ({ ...prev, gridSize: event.target.value, excluded: [] }))
                        }
                    />
                    <Select
                        label={t('AnalyticsVisibility.gridSpacingLabel')}
                        options={settings.gridSpacingOptions.map(meters => ({
                            value: meters,
                            label: t('AnalyticsVisibility.spacingKm', { km: meters / 1000 }),
                        }))}
                        value={draft.gridSpacingMeters}
                        disabled={saving}
                        onChange={event => setDraft(prev => ({ ...prev, gridSpacingMeters: event.target.value }))}
                    />
                </div>
                {center && (
                    <div style={{ marginTop: 16 }}>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ display: 'block', margin: '0 0 8px' }}
                        >
                            {t('AnalyticsVisibility.excludePinsHint')}
                        </Typography>
                        <GoogleMap
                            id="visibility-settings-map"
                            options={{ center, zoom: 12 }}
                            markers={markers}
                            onMarkerClick={togglePin}
                            style={{ height: 320, width: '100%' }}
                        />
                    </div>
                )}
            </>
        );
    };

    return (
        <StyledModal
            isOpen={open}
            handleClose={onClose}
            closeDisabled={saving}
            title={
                locationName
                    ? `${t('AnalyticsVisibility.gridSettings')} — ${locationName}`
                    : t('AnalyticsVisibility.gridSettings')
            }
            onPrimaryAction={editable ? onSave : undefined}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={saving || !draft?.keywords.length}
        >
            {renderContent()}
        </StyledModal>
    );
};

export default VisibilitySettingsModal;
